/**
 * Unified service for accepting hierarchical items
 * Maps hierarchy types to their respective API endpoints following hex architecture
 */

import { GoalService } from './goalService'
import { AspirationService } from './aspirationService'
import { DreamService } from './dreamService'
import { TaskService } from './taskService'
import { ActionItemService } from './actionItemService'
import { RoadblockService } from './roadblockService'
import { SkillService } from './skillService'
import { HabitService } from './habitService'
import { similarityService, type SimilarItem } from './similarityService'
import { nodeLinkService } from './nodeLinkService'

/**
 * Decision Signal Capture: field names the save path forwards from a
 * proposed item's `fields` (and any provenance under `decision_meta`) so the
 * backend can persist what AutoMom / the LifeMap prioritization engine need.
 * Mirrors the canonical catalog in
 * `src/domains/shared_utilities/services/decision_fields.py`; the backend
 * normalizes and drops fields that do not apply to a given type.
 */
const DECISION_FIELD_KEYS: string[] = [
  // Tier 1
  'life_area', 'deadline_type', 'deadline_consequence', 'deadline_risk_level',
  'energy_level_required', 'emotional_load', 'minimum_starter_step',
  'minimum_viable_repetition', 'motivation', 'why_it_matters',
  // Tier 2
  'difficulty', 'complexity_level', 'focus_level_required', 'is_quick_win',
  'momentum_hint', 'motivation_level', 'required_resources', 'location',
  'success_criteria', 'depends_on', 'blocks',
  // Tier 3
  'practice_cadence', 'review_checkpoint', 'identity_outcome',
  'stability_outcome', 'tools_needed', 'support_level', 'follow_through_mode',
]

export interface ResolveResult {
  matched: boolean
  node_id: number | null
  node_type: string | null
  title: string | null
  score: number
  confidence: 'high' | 'medium' | 'low'
  match_reasons: string[]
}

async function resolveEntity(
  queryText: string,
  entityType?: string,
  pageContext?: { entity_id: number; entity_type: string }
): Promise<ResolveResult> {
  try {
    const resp = await fetch('/api/hierarchy/resolve-entity', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        query_text: queryText,
        entity_type: entityType,
        page_context: pageContext,
      }),
    })
    if (!resp.ok) return { matched: false, node_id: null, node_type: null, title: null, score: 0, confidence: 'low', match_reasons: [] }
    return await resp.json()
  } catch {
    return { matched: false, node_id: null, node_type: null, title: null, score: 0, confidence: 'low', match_reasons: [] }
  }
}
/**
 * What saas-core says it did with a create.
 *
 * A duplicate create answers 201 with the id of a row that ALREADY EXISTED —
 * nothing was written. Until the dedup keys were forwarded past the controller
 * that response was byte-identical to a real create, so this file counted every
 * merge as `newlyCreated` and told the user "added 4 items" when it had added
 * two. `deduped_from_title` is the title the user asked for, which now has no
 * row of its own; `data.title` (the response's own `title`) is the survivor's.
 */
export interface CreateDedupInfo {
  deduped: boolean
  dedupedBy?: string          // "lexical" | "semantic"
  decidedBy?: string          // "exact_title_recent" | "exact_title" | "judge"
  score?: number
  fromTitle?: string          // what was asked for
  intoTitle?: string          // what survived
}

export function readCreateDedup(resp: any): CreateDedupInfo {
  // Tolerate both the flat body (current contract) and a `data`-wrapped one.
  const b = resp?.deduped !== undefined ? resp : (resp?.data ?? resp)
  if (!b?.deduped) return { deduped: false }
  return {
    deduped: true,
    dedupedBy: b.deduped_by,
    decidedBy: b.dedup_decided_by,
    score: typeof b.dedup_score === 'number' ? b.dedup_score : undefined,
    fromTitle: b.deduped_from_title,
    intoTitle: b.title,
  }
}

import type { HierarchyItem } from '../utils/itemValidation'
import type {
  DreamNode, AspirationNode, GoalNode, TaskNode, ActionItemNode,
  RoadblockNode, KnowledgeSkillNode
} from './hortonV3Service'

// Instantiate services (following hex architecture - single responsibility)
const goalService = new GoalService()
const aspirationService = new AspirationService()
const dreamService = new DreamService()
const taskService = new TaskService()
const actionItemService = new ActionItemService()
const roadblockService = new RoadblockService()
const skillService = new SkillService()
const habitService = new HabitService()

export interface AcceptItemRequest {
  title: string
  description?: string
  timeline?: string
  target_date?: string
  due_date?: string
  parent_aspiration_id?: number
  parent_dream_id?: number
  parent_goal_id?: number
  parent_task_id?: number
  status?: string
  priority?: 'low' | 'medium' | 'high' | 'critical' | 'urgent'
  category?: string
  [key: string]: any
}

export interface AcceptItemOptions {
  checkDuplicates?: boolean
  ensureParentExists?: boolean
  allItemsInConversation?: HierarchyItem[]
  onParentCreated?: (parentId: number, parentType: string) => void
  onSimilarItemsFound?: (similarItems: SimilarItem[]) => Promise<boolean> // Return true to proceed, false to cancel
  similarityThreshold?: number // Default: 0.75
}

/**
 * Map hierarchy type to API endpoint and service
 */
function mapHierarchyTypeToService(hierarchyType: string) {
  const mapping: Record<string, { endpoint: string; service: any }> = {
    'GOALS_OBJECTIVES': {
      endpoint: '/api/hierarchy/goals',
      service: goalService
    },
    'ASPIRATIONS_AMBITIONS': {
      endpoint: '/api/hierarchy/aspirations',
      service: aspirationService
    },
    'VISION_PURPOSE': {
      endpoint: '/api/hierarchy/dreams',
      service: dreamService
    },
    'DREAMS': {
      endpoint: '/api/hierarchy/dreams',
      service: dreamService
    },
    'TASKS_ACTIONS': {
      endpoint: '/api/hierarchy/tasks',
      service: taskService
    },
    'ACTION_ITEMS': {
      endpoint: '/api/hierarchy/action-items',
      service: actionItemService
    },
    'OBSTACLES_CHALLENGES': {
      endpoint: '/api/hierarchy/roadblocks',
      service: roadblockService
    },
    'ROADBLOCKS': {
      endpoint: '/api/hierarchy/roadblocks',
      service: roadblockService
    },
    'SKILLS': {
      endpoint: '/api/hierarchy/skills',
      service: skillService
    },
    'HABITS': {
      endpoint: '/api/hierarchy/habits',
      service: habitService
    }
  }
  
  return mapping[hierarchyType] || null
}

/**
 * Get default status for hierarchy type
 */
function getDefaultStatus(hierarchyType: string): string {
  const statusMap: Record<string, string> = {
    'GOALS_OBJECTIVES': 'active',
    'ASPIRATIONS_AMBITIONS': 'active',
    'VISION_PURPOSE': 'active',
    'DREAMS': 'active',
    'TASKS_ACTIONS': 'pending',
    'ACTION_ITEMS': 'active',
    'OBSTACLES_CHALLENGES': 'active',
    'ROADBLOCKS': 'active',
    'SKILLS': 'active',
    'HABITS': 'active'
  }
  return statusMap[hierarchyType] || 'active'
}

/**
 * Map status to valid status for hierarchy type
 */
function mapStatus(status: string | undefined, hierarchyType: string): string {
  if (!status || status === 'draft') {
    return getDefaultStatus(hierarchyType)
  }
  
  // Map invalid statuses to valid ones. 'parked' (rationing create-but-flag,
  // P1B) is valid for every type: the item is saved but excluded from active
  // surfaces until the user activates it from the parking lot.
  const validStatuses: Record<string, string[]> = {
    'GOALS_OBJECTIVES': ['active', 'completed', 'paused', 'cancelled', 'archived', 'parked'],
    'ASPIRATIONS_AMBITIONS': ['active', 'completed', 'paused', 'cancelled', 'parked'],
    'VISION_PURPOSE': ['active', 'completed', 'paused', 'cancelled', 'parked'],
    'DREAMS': ['active', 'completed', 'paused', 'cancelled', 'parked'],
    'TASKS_ACTIONS': ['pending', 'in_progress', 'completed', 'cancelled', 'on_hold', 'parked'],
    'ACTION_ITEMS': ['active', 'completed', 'paused', 'cancelled', 'parked'],
    'OBSTACLES_CHALLENGES': ['active', 'resolved', 'mitigated', 'blocked', 'monitoring', 'parked'],
    'ROADBLOCKS': ['active', 'resolved', 'mitigated', 'blocked', 'monitoring', 'parked'],
    'SKILLS': ['active', 'developing', 'ready_for_review', 'sufficient_for_goal', 'parked'],
    'HABITS': ['active', 'forming', 'stable', 'internalized', 'parked']
  }
  
  const valid = validStatuses[hierarchyType] || ['active']
  if (valid.includes(status)) {
    return status
  }
  
  // If status is invalid, return default
  return getDefaultStatus(hierarchyType)
}

/**
 * Transform item from life map format to API request format
 */
function transformItemToRequest(item: HierarchyItem, hierarchyType: string): AcceptItemRequest {
  const title = item.details?.clean_summary || item.details?.action || 'Untitled Item'
  const description = item.details?.description || item.details?.action || title
  const timeline = item.details?.timeline
  
  // Map status to valid status for this hierarchy type
  const mappedStatus = mapStatus(item.status, hierarchyType)
  
  const request: AcceptItemRequest = {
    title,
    description: description || title, // Ensure description exists (required for action items)
    status: mappedStatus
  }
  
  // For ACTION_ITEMS, description is required, so use title if description is empty
  if (hierarchyType === 'ACTION_ITEMS' && !description) {
    request.description = title
  }
  
  // Add timeline/target_date based on hierarchy type
  // Only include if timeline looks like a valid date (ISO format or parseable date)
  if (timeline) {
    // Try to parse as date - if it's a valid date string, include it
    // Otherwise, skip it (timeline might be free-form text like "Q1 2024")
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/ // ISO date format YYYY-MM-DD
    const isDateString = dateRegex.test(timeline.trim())
    
    if (isDateString) {
      if (hierarchyType === 'GOALS_OBJECTIVES' || hierarchyType === 'ASPIRATIONS_AMBITIONS' || hierarchyType === 'VISION_PURPOSE' || hierarchyType === 'DREAMS') {
        request.target_date = timeline.trim()
      } else if (hierarchyType === 'TASKS_ACTIONS' || hierarchyType === 'ACTION_ITEMS' || hierarchyType === 'OBSTACLES_CHALLENGES' || hierarchyType === 'ROADBLOCKS') {
        request.due_date = timeline.trim()
      }
    }
    // If timeline is not a valid date format, we skip it to avoid backend errors
    // The user can add proper dates later when editing the item
  }
  
  // Add parent relationships if available (only include valid parent IDs)
  if (item.details?.parent_aspiration_id) {
    request.parent_aspiration_id = item.details.parent_aspiration_id
  }
  if (item.details?.parent_dream_id) {
    request.parent_dream_id = item.details.parent_dream_id
  }
  if (item.details?.parent_goal_id) {
    request.parent_goal_id = item.details.parent_goal_id
  }
  if (item.details?.parent_task_id) {
    request.parent_task_id = item.details.parent_task_id
  }
  
  // Add scheduling_hint as ai_suggested_recurrence for action items
  // This stores the RRULE-based scheduling hint from the Planner LLM
  if (hierarchyType === 'ACTION_ITEMS' && item.details?.scheduling_hint) {
    const hint = item.details.scheduling_hint
    // Store as JSON string in ai_suggested_recurrence field
    request.ai_suggested_recurrence = JSON.stringify(hint)
  }
  
  // Add priority if available (map to valid priority values)
  if (item.details?.priority) {
    const priority = item.details.priority.toLowerCase()
    // Map priority to valid values based on hierarchy type
    if (hierarchyType === 'TASKS_ACTIONS') {
      // Tasks use: low, medium, high, urgent
      if (['low', 'medium', 'high', 'urgent'].includes(priority)) {
        request.priority = priority as 'low' | 'medium' | 'high' | 'urgent'
      } else {
        request.priority = 'medium'
      }
    } else if (hierarchyType === 'OBSTACLES_CHALLENGES' || hierarchyType === 'ROADBLOCKS') {
      // Roadblocks use: low, medium, high, critical
      if (['low', 'medium', 'high', 'critical'].includes(priority)) {
        request.priority = priority as 'low' | 'medium' | 'high' | 'critical'
      } else {
        request.priority = 'high' // Roadblocks default to high priority
      }
    } else {
      // Goals, Aspirations, Dreams use: low, medium, high, critical
      if (['low', 'medium', 'high', 'critical'].includes(priority)) {
        request.priority = priority as 'low' | 'medium' | 'high' | 'critical'
      } else {
        request.priority = 'medium'
      }
    }
  }
  
  // Add category if available
  if (item.details?.category) {
    request.category = item.details.category
  }
  
  // ─────────────────────────────────────────────────────────────────────────
  // SCHEDULING INTENT FIELDS (from planner - for tasks)
  // ─────────────────────────────────────────────────────────────────────────
  // These fields come from the planner's scheduling intent and should be
  // passed to the backend to create tasks with proper recurrence/scheduling
  if (hierarchyType === 'TASKS_ACTIONS') {
    const fields = item.fields || {}
    
    // Duration (might be in fields or details)
    if (fields.duration_minutes) {
      request.estimated_duration = fields.duration_minutes
    } else if (item.details?.duration_minutes) {
      request.estimated_duration = item.details.duration_minutes
    }
    
    // Schedule candidate flag
    if (fields.schedule_candidate !== undefined) {
      request.schedule_candidate = fields.schedule_candidate
    }
    
    // Preferred days (e.g., ["sun", "mon"])
    if (fields.preferred_days) {
      request.preferred_days = fields.preferred_days
    }
    
    // Preferred time window (e.g., "morning", "afternoon")
    if (fields.preferred_time_window) {
      request.preferred_time_window = fields.preferred_time_window
    }
    
    // Time constraints
    if (fields.earliest_start_time) {
      request.earliest_start_time = fields.earliest_start_time
    }
    if (fields.latest_end_time) {
      request.latest_end_time = fields.latest_end_time
    }
    
    // Recurrence (e.g., {type: "weekly", interval: 1, days_of_week: ["sun"]})
    if (fields.recurrence) {
      request.recurrence = fields.recurrence
    }
    
    console.log('📅 Task scheduling intent fields:', {
      preferred_days: fields.preferred_days,
      preferred_time_window: fields.preferred_time_window,
      recurrence: fields.recurrence
    })
  }
  
  return request
}

/**
 * Check for semantically similar items (not just exact duplicates)
 * PRIORITY: First checks for exact/near-exact title matches, then semantic similarity
 */
async function checkForSimilarItems(
  title: string, 
  description: string,
  hierarchyType: string,
  threshold: number = 0.75
): Promise<SimilarItem[]> {
  try {
    let items: any[] = []
    
    if (hierarchyType === 'GOALS_OBJECTIVES') {
      items = await goalService.getGoals()
    } else if (hierarchyType === 'ASPIRATIONS_AMBITIONS') {
      items = await aspirationService.getAspirations()
    } else if (hierarchyType === 'VISION_PURPOSE' || hierarchyType === 'DREAMS') {
      items = await dreamService.getDreams()
    } else if (hierarchyType === 'TASKS_ACTIONS') {
      items = await taskService.getTasks()
    } else if (hierarchyType === 'ACTION_ITEMS') {
      items = await actionItemService.getActionItems()
    } else if (hierarchyType === 'SKILLS') {
      items = await skillService.getSkills()
    } else if (hierarchyType === 'HABITS') {
      items = await habitService.getHabits()
    } else if (hierarchyType === 'ROADBLOCKS' || hierarchyType === 'OBSTACLES_CHALLENGES') {
      items = await roadblockService.getRoadblocks()
    }
    
    console.log(`🔍 Checking for duplicates: "${title}" (${hierarchyType}) among ${items.length} existing items`)
    
    if (items.length === 0) {
      console.log(`  ℹ️ No existing items of type ${hierarchyType}`)
      return []
    }
    
    // FIRST: Check for exact or near-exact title match (most reliable)
    const normalizedTitle = title.toLowerCase().trim()
    const exactMatches = items.filter(item => {
      const existingTitle = (item.title || '').toLowerCase().trim()
      // Check exact match, or if one contains the other (handles minor variations)
      return existingTitle === normalizedTitle ||
             (existingTitle.length > 5 && normalizedTitle.length > 5 && (
               existingTitle.includes(normalizedTitle) || 
               normalizedTitle.includes(existingTitle)
             ))
    })
    
    if (exactMatches.length > 0) {
      console.log(`  ✅ Found ${exactMatches.length} exact/near-exact match(es) for "${title}"`)
      return exactMatches.map(item => ({
        item,
        similarity_score: 1.0,
        similarity_category: 'identical' as const
      }))
    }
    
    // SECOND: Try semantic similarity service
    try {
      const result = await similarityService.checkSimilarItems({
        title,
        description,
        hierarchy_type: hierarchyType,
        existing_items: items,
        threshold
      })
      
      if (result.similar_items && result.similar_items.length > 0) {
        console.log(`  🔗 Found ${result.similar_items.length} semantically similar item(s)`)
        return result.similar_items
      }
    } catch (semanticError) {
      console.warn(`  ⚠️ Semantic similarity check failed:`, semanticError)
      // Continue to return empty array - no similar items found
    }
    
    console.log(`  ℹ️ No duplicates found for "${title}"`)
    return []
  } catch (error) {
    console.error('Error checking for similar items:', error)
    // If all else fails, try exact title match as final fallback
    return checkForExactDuplicateFallback(title, hierarchyType)
  }
}

/**
 * Fallback: Check for exact title match if semantic similarity fails
 */
async function checkForExactDuplicateFallback(title: string, hierarchyType: string): Promise<SimilarItem[]> {
  try {
    let items: any[] = []
    
    if (hierarchyType === 'GOALS_OBJECTIVES') {
      items = await goalService.getGoals()
    } else if (hierarchyType === 'ASPIRATIONS_AMBITIONS') {
      items = await aspirationService.getAspirations()
    } else if (hierarchyType === 'VISION_PURPOSE' || hierarchyType === 'DREAMS') {
      items = await dreamService.getDreams()
    } else if (hierarchyType === 'TASKS_ACTIONS') {
      items = await taskService.getTasks()
    } else if (hierarchyType === 'ACTION_ITEMS') {
      items = await actionItemService.getActionItems()
    } else if (hierarchyType === 'SKILLS') {
      items = await skillService.getSkills()
    } else if (hierarchyType === 'HABITS') {
      items = await habitService.getHabits()
    } else if (hierarchyType === 'ROADBLOCKS' || hierarchyType === 'OBSTACLES_CHALLENGES') {
      items = await roadblockService.getRoadblocks()
    }
    
    // Exact title match
    const normalizedTitle = title.toLowerCase().trim()
    const exactMatches = items.filter(item => 
      item.title.toLowerCase().trim() === normalizedTitle
    )
    
    return exactMatches.map(item => ({
      item,
      similarity_score: 1.0,
      similarity_category: 'identical' as const
    }))
  } catch (error) {
    console.error('Error in fallback duplicate check:', error)
    return []
  }
}

/**
 * Find parent item by title in conversation data
 */
function findParentInConversation(
  parentTitle: string | undefined, 
  parentType: string,
  allItems: HierarchyItem[]
): HierarchyItem | undefined {
  if (!parentTitle || !allItems || allItems.length === 0) return undefined
  
  const normalizedTitle = parentTitle.toLowerCase().trim()
  return allItems.find(item => {
    const itemTitle = (item.details?.clean_summary || item.details?.action || '').toLowerCase().trim()
    const itemClassification = (item.classification?.type || '').toLowerCase()
    const matchesTitle = itemTitle === normalizedTitle
    const matchesType = itemClassification.includes(parentType.toLowerCase())
    return matchesTitle && matchesType
  })
}

/**
 * Ensure parent item exists, creating it if necessary
 */
async function ensureParentExists(
  item: HierarchyItem,
  request: AcceptItemRequest,
  options: AcceptItemOptions
): Promise<AcceptItemRequest> {
  if (!options.ensureParentExists || !options.allItemsInConversation) {
    return request
  }
  
  const updatedRequest = { ...request }
  
  // Check each parent type
  const parentChecks = [
    { 
      field: 'parent_dream_id', 
      type: 'DREAMS', 
      title: item.details?.parent_dream_title 
    },
    { 
      field: 'parent_aspiration_id', 
      type: 'ASPIRATIONS_AMBITIONS', 
      title: item.details?.parent_aspiration_title 
    },
    { 
      field: 'parent_goal_id', 
      type: 'GOALS_OBJECTIVES', 
      title: item.details?.parent_goal_title 
    },
    { 
      field: 'parent_task_id', 
      type: 'TASKS_ACTIONS', 
      title: item.details?.parent_task_title 
    }
  ]
  
  for (const parentCheck of parentChecks) {
    const parentIdField = parentCheck.field as keyof AcceptItemRequest
    const parentId = request[parentIdField]
    
    // If parent ID is referenced but parent might not exist, try to create it
    if (parentId && parentCheck.title) {
      const parentItem = findParentInConversation(
        parentCheck.title, 
        parentCheck.type, 
        options.allItemsInConversation
      )
      
      if (parentItem) {
        try {
          // Create the parent item first
          const createdParent = await acceptHierarchyItem(parentItem, parentCheck.type, {
            checkDuplicates: true,
            ensureParentExists: true,
            allItemsInConversation: options.allItemsInConversation,
            onParentCreated: options.onParentCreated
          })
          
          // Update the parent ID with the newly created item's ID
          const newParentId = createdParent?.id || createdParent?.data?.id
          if (newParentId) {
            updatedRequest[parentIdField] = newParentId as any
            
            // Notify callback
            if (options.onParentCreated) {
              options.onParentCreated(newParentId, parentCheck.type)
            }
          }
        } catch (error) {
          console.warn(`Failed to create parent ${parentCheck.type}:`, error)
          // Remove the parent reference if creation fails
          delete updatedRequest[parentIdField]
        }
      } else {
        // Parent not found in conversation, remove the reference
        console.warn(`Parent ${parentCheck.type} not found in conversation data`)
        delete updatedRequest[parentIdField]
      }
    }
  }
  
  return updatedRequest
}

/**
 * Accept a hierarchical item by creating it via the appropriate API
 */
export async function acceptHierarchyItem(
  item: HierarchyItem, 
  hierarchyType: string,
  options: AcceptItemOptions = {}
): Promise<any> {
  const mapping = mapHierarchyTypeToService(hierarchyType)
  
  if (!mapping) {
    throw new Error(`Unknown hierarchy type: ${hierarchyType}`)
  }
  
  let request = transformItemToRequest(item, hierarchyType)
  
  // Check for similar items (semantic similarity) if enabled
  if (options.checkDuplicates !== false) { // Default to true
    const threshold = options.similarityThreshold || 0.75
    const similarItems = await checkForSimilarItems(
      request.title, 
      request.description || '',
      hierarchyType,
      threshold
    )
    
    if (similarItems.length > 0) {
      // If callback provided, let user decide
      if (options.onSimilarItemsFound) {
        const shouldProceed = await options.onSimilarItemsFound(similarItems)
        if (!shouldProceed) {
          throw new Error('Item creation cancelled by user')
        }
      } else {
        // No callback, throw error with similar items info
        const topMatch = similarItems[0]
        const categoryLabel = similarityService.getCategoryLabel(topMatch.similarity_category)
        throw new Error(
          `Found ${categoryLabel.toLowerCase()} item: "${topMatch.item.title}" ` +
          `(${Math.round(topMatch.similarity_score * 100)}% match). ` +
          `Please check if this is what you meant.`
        )
      }
    }
  }
  
  // Ensure parent exists if enabled
  if (options.ensureParentExists) {
    request = await ensureParentExists(item, request, options)
  }
  
  // Use the appropriate service method
  if (hierarchyType === 'GOALS_OBJECTIVES') {
    return await goalService.createGoal(request as any)
  } else if (hierarchyType === 'ASPIRATIONS_AMBITIONS') {
    return await aspirationService.createAspiration(request as any)
  } else if (hierarchyType === 'VISION_PURPOSE' || hierarchyType === 'DREAMS') {
    return await dreamService.createDream(request as any)
  } else if (hierarchyType === 'TASKS_ACTIONS') {
    return await taskService.createTask(request as any)
  } else if (hierarchyType === 'ACTION_ITEMS') {
    return await actionItemService.createActionItem(request as any)
  } else if (hierarchyType === 'OBSTACLES_CHALLENGES' || hierarchyType === 'ROADBLOCKS') {
    return await roadblockService.createRoadblock(request as any)
  } else if (hierarchyType === 'SKILLS') {
    return await skillService.createSkill(request as any)
  } else if (hierarchyType === 'HABITS') {
    return await habitService.createHabit(request as any)
  }
  
  throw new Error(`Unsupported hierarchy type: ${hierarchyType}`)
}

// ============================================================================
// V3 HIERARCHY CHAIN SUPPORT
// ============================================================================

/**
 * Represents an item in the parent chain with its type and data
 */
export interface ParentChainItem {
  type: 'dream' | 'aspiration' | 'goal' | 'task' | 'action_item' | 'roadblock' | 'skill' | 'habit'
  hierarchyType: string
  title: string
  description?: string
  life_area?: string
  priority?: string
  duration_minutes?: number
  severity?: 'low' | 'medium' | 'high'
  suggested_solutions?: string[]
  current_level?: string
  target_level?: string
  learning_steps?: string[]
  // Decision Signal Capture: the source node's raw `fields` bag and
  // provenance map, preserved so the parent-chain save path forwards
  // decision signals just like the single-item save path. Without this
  // the signals yanamas captured on ancestor items are silently dropped.
  fields?: Record<string, any>
  decision_meta?: Record<string, any>
  createdId?: number  // Populated after creation
}

/**
 * Result of accepting a V3 hierarchy item with parent chain
 */
export interface AcceptV3ItemResult {
  success: boolean
  targetItem?: any  // The item that was requested to be accepted
  createdItems: Array<{
    type: string
    id: number
    title: string
    isExisting?: boolean
    /** Backend resolver provenance when an existing node was reused */
    resolverConfidence?: string
    resolverScore?: number
    /** saas-core merged this create onto an existing row (see readCreateDedup) */
    dedup?: CreateDedupInfo
  }>
  errors: string[]
  stats?: {
    newlyCreated: number
    reusedExisting: number
  }
}

/**
 * Options for accepting V3 hierarchy items
 */
export interface AcceptV3ItemOptions {
  skipDuplicateCheck?: boolean
  onItemCreated?: (type: string, id: number, title: string) => void
  onSimilarItemsFound?: (similarItems: SimilarItem[]) => Promise<boolean>
  similarityThreshold?: number
}

/**
 * Map V3 type to internal hierarchy type constant
 */
function mapV3TypeToHierarchyType(type: string): string {
  const typeMap: Record<string, string> = {
    'dream': 'DREAMS',
    'aspiration': 'ASPIRATIONS_AMBITIONS',
    'goal': 'GOALS_OBJECTIVES',
    'task': 'TASKS_ACTIONS',
    'action_item': 'ACTION_ITEMS',
    'roadblock': 'ROADBLOCKS',
    'skill': 'SKILLS',
    'habit': 'HABITS'
  }
  return typeMap[type] || 'GOALS_OBJECTIVES'
}

/**
 * Extract parent chain from a V3 nested hierarchy structure.
 * Given an items_hierarchy and a target item, traces back all parents up to the root dream.
 * 
 * @param itemsHierarchy - The full V3 items_hierarchy array (array of DreamNodes)
 * @param targetItem - The item we want to accept (could be at any level)
 * @param targetType - The type of the target item
 * @returns Array of ParentChainItems from root (dream) to target item
 */
export function extractParentChainFromV3Hierarchy(
  itemsHierarchy: DreamNode[],
  targetItem: DreamNode | AspirationNode | GoalNode | TaskNode | ActionItemNode | RoadblockNode | KnowledgeSkillNode,
  targetType: string
): ParentChainItem[] {
  // Helper to convert a V3 node to ParentChainItem
  const nodeToChainItem = (node: any, type: string): ParentChainItem => ({
    type: type as ParentChainItem['type'],
    hierarchyType: mapV3TypeToHierarchyType(type),
    title: node.title,
    description: node.description,
    life_area: node.life_area,
    priority: node.priority,
    duration_minutes: node.duration_minutes,
    severity: node.severity,
    suggested_solutions: node.suggested_solutions,
    current_level: node.current_level,
    target_level: node.target_level,
    learning_steps: node.learning_steps,
    fields: node.fields,
    decision_meta: node.decision_meta ?? node.fields?.decision_meta
  })
  
  // Recursively search for the target item and build the chain
  const findAndBuildChain = (
    currentItems: any[], 
    currentType: string,
    pathSoFar: ParentChainItem[]
  ): ParentChainItem[] | null => {
    for (const item of currentItems) {
      const currentPath = [...pathSoFar, nodeToChainItem(item, currentType)]
      
      // Check if this is the target
      if (item.title === targetItem.title && currentType === targetType) {
        return currentPath
      }
      
      // Search children based on type
      if (currentType === 'dream') {
        const dreamNode = item as DreamNode
        // Check aspirations
        if (dreamNode.aspirations) {
          const result = findAndBuildChain(dreamNode.aspirations, 'aspiration', currentPath)
          if (result) return result
        }
        // Check direct goals under dream
        if (dreamNode.goals) {
          const result = findAndBuildChain(dreamNode.goals, 'goal', currentPath)
          if (result) return result
        }
      } else if (currentType === 'aspiration') {
        const aspNode = item as AspirationNode
        if (aspNode.goals) {
          const result = findAndBuildChain(aspNode.goals, 'goal', currentPath)
          if (result) return result
        }
      } else if (currentType === 'goal') {
        const goalNode = item as GoalNode
        if (goalNode.tasks) {
          const result = findAndBuildChain(goalNode.tasks, 'task', currentPath)
          if (result) return result
        }
        if (goalNode.roadblocks) {
          const result = findAndBuildChain(goalNode.roadblocks, 'roadblock', currentPath)
          if (result) return result
        }
        if ((goalNode as any).skills) {
          const result = findAndBuildChain((goalNode as any).skills, 'skill', currentPath)
          if (result) return result
        }
        if ((goalNode as any).habits) {
          const result = findAndBuildChain((goalNode as any).habits, 'habit', currentPath)
          if (result) return result
        }
      } else if (currentType === 'task') {
        const taskNode = item as TaskNode
        if (taskNode.action_items) {
          const result = findAndBuildChain(taskNode.action_items, 'action_item', currentPath)
          if (result) return result
        }
      }
    }
    return null
  }
  
  // Start search from dreams
  const foundChain = findAndBuildChain(itemsHierarchy, 'dream', [])
  return foundChain || [nodeToChainItem(targetItem, targetType)]
}

/**
 * Accept a V3 hierarchy item along with all its parent items.
 * Creates items from the top of the hierarchy down, linking each child to its parent.
 * 
 * @param itemsHierarchy - The full V3 items_hierarchy for context
 * @param targetItem - The item the user wants to accept
 * @param targetType - The type of the target item
 * @param options - Options for controlling the accept process
 * @returns Result containing all created items
 */
/**
 * Accept an entire subtree: the root item + ALL its descendants (children, grandchildren, etc.)
 * This is used for the "Accept All" button.
 */
export async function acceptV3EntireSubtree(
  itemsHierarchy: DreamNode[],
  rootItem: any,
  rootItemType: string,
  options: AcceptV3ItemOptions = {}
): Promise<AcceptV3ItemResult> {
  console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`)
  console.log(`🌳 acceptV3EntireSubtree: Starting from ${rootItemType} "${rootItem.title}"`)
  console.log(`📦 Root item structure:`, rootItem)
  console.log(`📊 Root item keys:`, Object.keys(rootItem))
  console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`)
  
  // Check for children properties
  console.log(`🔍 Checking for children in root item:`)
  if (rootItemType === 'dream') {
    console.log(`  - aspirations: ${rootItem.aspirations ? rootItem.aspirations.length : 'undefined'}`)
    console.log(`  - goals: ${rootItem.goals ? rootItem.goals.length : 'undefined'}`)
    if (rootItem.aspirations) console.log(`  - aspirations content:`, rootItem.aspirations)
    if (rootItem.goals) console.log(`  - goals content:`, rootItem.goals)
  } else if (rootItemType === 'aspiration') {
    console.log(`  - goals: ${rootItem.goals ? rootItem.goals.length : 'undefined'}`)
    if (rootItem.goals) console.log(`  - goals content:`, rootItem.goals)
  } else if (rootItemType === 'goal') {
    console.log(`  - tasks: ${rootItem.tasks ? rootItem.tasks.length : 'undefined'}`)
    console.log(`  - roadblocks: ${rootItem.roadblocks ? rootItem.roadblocks.length : 'undefined'}`)
    if (rootItem.tasks) console.log(`  - tasks content:`, rootItem.tasks)
    if (rootItem.roadblocks) console.log(`  - roadblocks content:`, rootItem.roadblocks)
  } else if (rootItemType === 'task') {
    console.log(`  - action_items: ${rootItem.action_items ? rootItem.action_items.length : 'undefined'}`)
    if (rootItem.action_items) console.log(`  - action_items content:`, rootItem.action_items)
  }
  console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`)
  
  const result: AcceptV3ItemResult = {
    success: true,
    createdItems: [],
    errors: [],
    stats: {
      newlyCreated: 0,
      reusedExisting: 0
    }
  }
  
  // Track created IDs for linking children to parents
  const createdIds: Record<string, number> = {}
  
  /**
   * Recursively accept an item and all its children
   * @param item The item to accept
   * @param itemType The type of the item
   * @param isRoot True if this is the root item (accept with parents), false otherwise (accept only)
   * @param directParent The node this item sits directly under in the subtree walk
   *                     (used to link reused nodes to their ACTUAL parent, not the top ancestor)
   */
  async function acceptItemAndChildren(
    item: any,
    itemType: string,
    isRoot: boolean = false,
    directParent?: { type: string; id: number }
  ): Promise<number | null> {
    try {
      console.log(`📦 Processing ${itemType}: "${item.title}" (isRoot: ${isRoot})`)
      
      let itemId: number | null = null
      
      if (isRoot) {
        // For the root item, accept it WITH its parents (creates full parent chain)
        console.log(`  🔗 Accepting ${itemType} WITH parents...`)
        const itemResult = await acceptV3HierarchyItemWithParents(
          itemsHierarchy,
          item,
          itemType,
          {
            ...options,
            onItemCreated: undefined // We'll handle this ourselves
          }
        )
        
        if (!itemResult.success || itemResult.createdItems.length === 0) {
          throw new Error(`Failed to create ${itemType} "${item.title}"`)
        }
        
        // Add all items from the parent chain to result
        result.createdItems.push(...itemResult.createdItems)
        
        // The root item is the LAST one in the chain
        const createdItem = itemResult.createdItems[itemResult.createdItems.length - 1]
        itemId = createdItem.id
        
        // Track all IDs from the parent chain
        for (const created of itemResult.createdItems) {
          createdIds[created.type] = created.id
          if (options.onItemCreated) {
            options.onItemCreated(created.type, created.id, created.title)
          }
        }
        
      } else {
        // For children, accept ONLY this item (no parent chain needed)
        // Manually set parent IDs based on what we've created
        console.log(`  ➕ Accepting ${itemType} only (will link to parents)...`)
        
        const hierarchyType = mapV3TypeToHierarchyType(itemType)
        const serviceMapping = mapHierarchyTypeToService(hierarchyType)
        
        if (!serviceMapping) {
          throw new Error(`No service mapping for ${itemType}`)
        }
        
        // FIRST: Check if this item already exists (backend resolver + frontend fallback)
        if (!options.skipDuplicateCheck) {
          try {
            // Backend resolver (pgvector hybrid search) – the true guardrail
            const resolveResult = await resolveEntity(item.title, itemType)

            if (resolveResult.matched && resolveResult.node_id &&
                (resolveResult.confidence === 'high' || resolveResult.confidence === 'medium')) {
              console.log(`  ♻️ RESOLVER: reusing ${resolveResult.node_type} ID ${resolveResult.node_id} (score=${resolveResult.score}, ${resolveResult.confidence})`)
              itemId = resolveResult.node_id
              createdIds[itemType] = resolveResult.node_id

              result.createdItems.push({
                type: itemType,
                id: resolveResult.node_id,
                title: resolveResult.title || item.title,
                isExisting: true,
                resolverConfidence: resolveResult.confidence,
                resolverScore: resolveResult.score,
              })

              if (result.stats) { result.stats.reusedExisting++ }
              if (options.onItemCreated) {
                options.onItemCreated(itemType, resolveResult.node_id, resolveResult.title || item.title)
              }

              // Create a link when reusing a node in a new context.
              // Link under the node's DIRECT parent in this subtree — not an
              // arbitrary ancestor from createdIds insertion order.
              if (directParent) {
                try {
                  let relType = 'related_to'
                  if (itemType === 'roadblock') {
                    relType = 'blocks'
                  } else if (['habit', 'skill', 'task'].includes(itemType) &&
                             ['goal', 'dream'].includes(directParent.type)) {
                    relType = 'supports'
                  }
                  await nodeLinkService.createLink(
                    resolveResult.node_id, itemType,
                    directParent.id, directParent.type,
                    relType, 'secondary', 'chat',
                    { route: 'chat' },
                  )
                } catch { /* best-effort link creation */ }
              }
            } else {
              // Fallback: frontend similarity check (UX aid)
              const similarItems = await checkForSimilarItems(
                item.title,
                item.description || '',
                hierarchyType,
                options.similarityThreshold || 0.75
              )

              if (similarItems.length > 0) {
                const existingItem = similarItems[0].item
                console.log(`  ♻️ FRONTEND DEDUP: reusing ${itemType}: "${existingItem.title}" (ID: ${existingItem.id})`)

                itemId = existingItem.id
                createdIds[itemType] = existingItem.id

                result.createdItems.push({
                  type: itemType,
                  id: existingItem.id,
                  title: existingItem.title,
                  isExisting: true,
                })

                if (result.stats) { result.stats.reusedExisting++ }
                if (options.onItemCreated) {
                  options.onItemCreated(itemType, existingItem.id, existingItem.title)
                }
              }
            }
          } catch (dupError) {
            console.warn(`  ⚠️ Duplicate check failed for ${itemType}:`, dupError)
          }
        }
        
        // If we found an existing item, skip creation but still process children
        if (itemId) {
          // Already have itemId from duplicate check - skip to children
          console.log(`  ⏭️ Skipping creation (using existing ID: ${itemId}), will process children...`)
        } else {
          // Extract fields from item (may contain scheduling intent from planner)
          const fields = item.fields || {}
          
          // Build request with parent IDs
          const request: any = {
            title: item.title,
            description: item.description,
            status: item.status || 'active',
            priority: fields.priority || item.priority || 'medium',
            timeline: item.timeline,
            target_date: item.target_date,
            due_date: item.due_date,
            
            // Include scheduling intent fields from planner (for tasks)
            estimated_duration: fields.duration_minutes,
            preferred_days: fields.preferred_days,
            preferred_time_window: fields.preferred_time_window,
            earliest_start_time: fields.earliest_start_time,
            latest_end_time: fields.latest_end_time,
            schedule_candidate: fields.schedule_candidate,
            
            // Rich recurrence object from planner
            recurrence: fields.recurrence
          }

          // Decision Signal Capture: forward decision fields + provenance
          // (decision_meta) so the backend persists what AutoMom / the
          // LifeMap engine need. The repository normalizes and drops fields
          // that don't apply to the type, so forwarding a superset is safe.
          for (const key of DECISION_FIELD_KEYS) {
            const v = fields[key] ?? (item as any)[key]
            if (v !== undefined && v !== null) request[key] = v
          }
          const decisionMeta = fields.decision_meta ?? (item as any).decision_meta
          if (decisionMeta && typeof decisionMeta === 'object') {
            request.decision_meta = decisionMeta
          }

          // Link to parent based on item type
          if (itemType === 'aspiration' && createdIds['dream']) {
            request.parent_dream_id = createdIds['dream']
            console.log(`    🔗 Linking to dream ${createdIds['dream']}`)
          } else if (itemType === 'goal') {
            if (createdIds['aspiration']) {
              request.parent_aspiration_id = createdIds['aspiration']
              console.log(`    🔗 Linking to aspiration ${createdIds['aspiration']}`)
            }
            if (createdIds['dream']) {
              request.parent_dream_id = createdIds['dream']
              console.log(`    🔗 Linking to dream ${createdIds['dream']}`)
            }
          } else if (itemType === 'task') {
            if (createdIds['goal']) {
              request.parent_goal_id = createdIds['goal']
              console.log(`    🔗 Linking to goal ${createdIds['goal']}`)
            }
            if (createdIds['aspiration']) {
              request.parent_aspiration_id = createdIds['aspiration']
            }
            if (createdIds['dream']) {
              request.parent_dream_id = createdIds['dream']
            }
            // Practice tasks may sit under a skill in chat-engine v5 proposals (memory proposer → saas_core_legacy items_hierarchy).
            if (createdIds['skill']) {
              request.parent_skill_id = createdIds['skill']
              console.log(`    🔗 Linking task to skill ${createdIds['skill']}`)
            }
          } else if (itemType === 'action_item' && createdIds['task']) {
            request.parent_task_id = createdIds['task']
            console.log(`    🔗 Linking to task ${createdIds['task']}`)
          } else if (itemType === 'roadblock' && createdIds['goal']) {
            request.parent_goal_id = createdIds['goal']
            console.log(`    🔗 Linking to goal ${createdIds['goal']}`)
          } else if (itemType === 'skill' && createdIds['goal']) {
            request.parent_goal_id = createdIds['goal']
            console.log(`    🔗 Linking skill to goal ${createdIds['goal']}`)
          } else if (itemType === 'habit' && createdIds['goal']) {
            request.parent_goal_id = createdIds['goal']
            console.log(`    🔗 Linking habit to goal ${createdIds['goal']}`)
          }

          // Habit-specific: habits use `duration_minutes` (not `estimated_duration`)
          // and also accept `preferred_start_time` ("HH:MM") as a suggested start.
          // We still let the user refine in HabitScheduleEditor, but we must not
          // silently drop the values the planner suggested.
          if (itemType === 'habit') {
            if (fields.target_cadence) request.target_cadence = fields.target_cadence
            if (fields.habit_status) request.habit_status = fields.habit_status
            if (fields.rrule) request.rrule = fields.rrule
            if (fields.nag_style) request.nag_style = fields.nag_style
            // Route duration under the correct habit field name and stop leaking
            // the task-shaped `estimated_duration` into the habit payload.
            delete request.estimated_duration
            if (typeof fields.duration_minutes === 'number' && fields.duration_minutes > 0) {
              request.duration_minutes = fields.duration_minutes
            }
            if (fields.preferred_start_time) {
              request.preferred_start_time = fields.preferred_start_time
            }
            console.log(`    📋 Habit created with duration_minutes=${request.duration_minutes ?? 'unset'} preferred_start_time=${request.preferred_start_time ?? 'unset'}`)
          }

          // Skill-specific: forward status/level fields (edit form + planner)
          if (itemType === 'skill') {
            if (fields.skill_status || item.skill_status) request.skill_status = fields.skill_status || item.skill_status
            if (fields.current_level || item.current_level) request.current_level = fields.current_level || item.current_level
            if (fields.target_level || item.target_level) request.target_level = fields.target_level || item.target_level
          }

          // Log scheduling intent fields for debugging
          if (itemType === 'task' && (fields.recurrence || fields.preferred_days)) {
            console.log(`    📅 Task scheduling intent fields:`, {
              preferred_days: fields.preferred_days,
              preferred_time_window: fields.preferred_time_window,
              recurrence: fields.recurrence,
              duration_minutes: fields.duration_minutes,
              schedule_candidate: fields.schedule_candidate
            })
          }
          
          console.log(`    📤 Sending request for ${itemType}:`, JSON.stringify(request, null, 2))
        
          // Create the item using the correct service method
          let createdItem: any
          try {
            if (itemType === 'dream') {
              console.log(`    🔨 Calling dreamService.createDream()`)
              createdItem = await dreamService.createDream(request)
            } else if (itemType === 'aspiration') {
              console.log(`    🔨 Calling aspirationService.createAspiration()`)
              createdItem = await aspirationService.createAspiration(request)
            } else if (itemType === 'goal') {
              console.log(`    🔨 Calling goalService.createGoal()`)
              createdItem = await goalService.createGoal(request)
            } else if (itemType === 'task') {
              console.log(`    🔨 Calling taskService.createTask()`)
              createdItem = await taskService.createTask(request)
            } else if (itemType === 'action_item') {
              console.log(`    🔨 Calling actionItemService.createActionItem()`)
              createdItem = await actionItemService.createActionItem(request)
            } else if (itemType === 'roadblock') {
              console.log(`    🔨 Calling roadblockService.createRoadblock()`)
              createdItem = await roadblockService.createRoadblock(request)
            } else if (itemType === 'skill') {
              console.log(`    🔨 Calling skillService.createSkill()`)
              createdItem = await skillService.createSkill(request)
            } else if (itemType === 'habit') {
              console.log(`    🔨 Calling habitService.createHabit()`)
              createdItem = await habitService.createHabit(request)
            } else if (itemType === 'identity' || itemType === 'person') {
              // Identity/person nodes are informational — displayed in chat
              // but not persisted as hierarchy items. Skip gracefully.
              console.log(`    ℹ️ Skipping ${itemType} "${item.title}" (informational, not a hierarchy item)`)
              return null
            } else {
              throw new Error(`Unknown item type: ${itemType}`)
            }
          } catch (serviceError: any) {
            console.error(`    ❌ Service call failed for ${itemType}:`, serviceError)
            console.error(`    ❌ Error message:`, serviceError.message)
            console.error(`    ❌ Error stack:`, serviceError.stack)
            throw serviceError
          }
        
          itemId = createdItem?.id || createdItem?.data?.id
          console.log(`    📥 Response for ${itemType}:`, JSON.stringify(createdItem, null, 2))
          console.log(`    🆔 Extracted ID: ${itemId}`)
        
          if (!itemId) {
            throw new Error(`Failed to create ${itemType} "${item.title}" - no ID returned`)
          }
        
          // saas-core may have answered this create by REUSING a row that
          // already existed (exact title inside the type's window, or the
          // semantic resolver + judge). Nothing was written in that case, so
          // counting it as `newlyCreated` overstated what the user got.
          const dedup = readCreateDedup(createdItem)
          if (dedup.deduped) {
            console.log(`  ♻️  MERGED ${itemType}: "${item.title}" onto existing #${itemId} `
              + `("${dedup.intoTitle}", by ${dedup.dedupedBy}/${dedup.decidedBy})`)
          } else {
            console.log(`  🆕 CREATED new ${itemType}: "${item.title}" (ID: ${itemId})`)
          }

          // Add to result
          result.createdItems.push({
            type: itemType,
            id: itemId,
            title: item.title,
            isExisting: dedup.deduped,
            ...(dedup.deduped ? { dedup } : {})
          })

          // Track stats
          if (result.stats) {
            if (dedup.deduped) result.stats.reusedExisting++
            else result.stats.newlyCreated++
          }
        
          // Track this ID
          createdIds[itemType] = itemId
        
          if (options.onItemCreated) {
            options.onItemCreated(itemType, itemId, item.title)
          }
        }
      }
      
      console.log(`✅ Processed ${itemType} "${item.title}" (ID: ${itemId})`)
      
      // Now recursively accept all children
      console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`)
      console.log(`  🔍 Looking for children of ${itemType} "${item.title}"...`)
      console.log(`  📋 ORIGINAL Item object:`, item)
      console.log(`  📋 Item properties (keys):`, Object.keys(item))
      console.log(`  📋 Item.action_items =`, item.action_items)
      console.log(`  📋 Item.tasks =`, item.tasks)
      console.log(`  📋 Item.goals =`, item.goals)
      console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`)
      
      const childArrays: Array<{ items: any[], childType: string }> = []
      
      if (itemType === 'dream') {
        console.log(`    Checking dream children: aspirations=${!!item.aspirations}, goals=${!!item.goals}`)
        if (item.aspirations && Array.isArray(item.aspirations)) {
          console.log(`    ✅ Found ${item.aspirations.length} aspirations`)
          childArrays.push({ items: item.aspirations, childType: 'aspiration' })
        } else {
          console.log(`    ❌ No aspirations found (value:`, item.aspirations, `)`)
        }
        if (item.goals && Array.isArray(item.goals)) {
          console.log(`    ✅ Found ${item.goals.length} goals`)
          childArrays.push({ items: item.goals, childType: 'goal' })
        } else {
          console.log(`    ❌ No goals found (value:`, item.goals, `)`)
        }
      } else if (itemType === 'aspiration') {
        console.log(`    Checking aspiration children: goals=${!!item.goals}`)
        if (item.goals && Array.isArray(item.goals)) {
          console.log(`    ✅ Found ${item.goals.length} goals`)
          childArrays.push({ items: item.goals, childType: 'goal' })
        } else {
          console.log(`    ❌ No goals found (value:`, item.goals, `)`)
        }
      } else if (itemType === 'goal') {
        console.log(`    Checking goal children: tasks=${!!item.tasks}, action_items=${!!item.action_items}, roadblocks=${!!item.roadblocks}, skills=${!!item.skills}, habits=${!!item.habits}`)
        if (item.tasks && Array.isArray(item.tasks)) {
          console.log(`    ✅ Found ${item.tasks.length} tasks`)
          childArrays.push({ items: item.tasks, childType: 'task' })
        } else {
          console.log(`    ❌ No tasks found (value:`, item.tasks, `)`)
        }
        // CRITICAL FIX: Goals can have action_items directly nested (V4 format)
        if (item.action_items && Array.isArray(item.action_items)) {
          console.log(`    ✅ Found ${item.action_items.length} action_items directly under goal`)
          childArrays.push({ items: item.action_items, childType: 'action_item' })
        } else {
          console.log(`    ❌ No action_items found (value:`, item.action_items, `)`)
        }
        if (item.roadblocks && Array.isArray(item.roadblocks)) {
          console.log(`    ✅ Found ${item.roadblocks.length} roadblocks`)
          childArrays.push({ items: item.roadblocks, childType: 'roadblock' })
        } else {
          console.log(`    ❌ No roadblocks found (value:`, item.roadblocks, `)`)
        }
        if (item.skills && Array.isArray(item.skills)) {
          console.log(`    ✅ Found ${item.skills.length} skills`)
          childArrays.push({ items: item.skills, childType: 'skill' })
        } else {
          console.log(`    ❌ No skills found (value:`, item.skills, `)`)
        }
        if (item.habits && Array.isArray(item.habits)) {
          console.log(`    ✅ Found ${item.habits.length} habits`)
          childArrays.push({ items: item.habits, childType: 'habit' })
        } else {
          console.log(`    ❌ No habits found (value:`, item.habits, `)`)
        }
      } else if (itemType === 'skill') {
        // Skills are support nodes; planner/chat may still attach practice tasks here (or via journey).
        console.log(`    Checking skill children: tasks=${!!item.tasks}, habits=${!!item.habits}`)
        if (item.tasks && Array.isArray(item.tasks)) {
          console.log(`    ✅ Found ${item.tasks.length} tasks under skill`)
          childArrays.push({ items: item.tasks, childType: 'task' })
        } else {
          console.log(`    ❌ No tasks under skill (value:`, item.tasks, `)`)
        }
        if (item.habits && Array.isArray(item.habits)) {
          console.log(`    ✅ Found ${item.habits.length} habits under skill`)
          childArrays.push({ items: item.habits, childType: 'habit' })
        }
      } else if (itemType === 'task') {
        console.log(`    Checking task children: action_items=${!!item.action_items}`)
        if (item.action_items && Array.isArray(item.action_items)) {
          console.log(`    ✅ Found ${item.action_items.length} action_items`)
          childArrays.push({ items: item.action_items, childType: 'action_item' })
        } else {
          console.log(`    ❌ No action_items found (value:`, item.action_items, `)`)
        }
      }
      
      console.log(`  📊 Total child arrays to process: ${childArrays.length}`)
      
      // Process each child array
      for (const { items, childType } of childArrays) {
        console.log(`  📂 Processing ${items.length} ${childType}(s) under ${itemType} "${item.title}"`)
        
        for (let i = 0; i < items.length; i++) {
          const childItem = items[i]
          console.log(`    🔸 Processing child ${i + 1}/${items.length}: ${childType} "${childItem.title}"`)
          
          // Save current parent ID before recursing (in case children have children)
          const savedParentId = createdIds[itemType]
          console.log(`    💾 Saved parent ID for ${itemType}: ${savedParentId}`)

          // Recursively accept child (not root, so will be linked to parents).
          // Pass the current item as the child's DIRECT parent.
          await acceptItemAndChildren(
            childItem,
            childType,
            false,
            itemId != null ? { type: itemType, id: itemId } : undefined
          )
          
          // Restore parent ID (in case it got overwritten by grandchildren)
          createdIds[itemType] = savedParentId
          console.log(`    🔄 Restored parent ID for ${itemType}: ${savedParentId}`)
        }
      }
      
      if (childArrays.length === 0) {
        console.log(`  🍃 No children found for ${itemType} "${item.title}" - this is a leaf node`)
      }
      
      return itemId
      
    } catch (error: any) {
      console.error(`❌ Error accepting ${itemType} "${item.title}":`, error)
      result.errors.push(`${itemType} "${item.title}": ${error.message || 'Unknown error'}`)
      result.success = false
      return null
    }
  }
  
  // Start the recursive acceptance from the root (isRoot = true)
  await acceptItemAndChildren(rootItem, rootItemType, true)
  
  console.log(`🌳 acceptV3EntireSubtree complete:`)
  console.log(`   📊 Total items: ${result.createdItems.length}`)
  console.log(`   🆕 Newly created: ${result.stats?.newlyCreated || 0}`)
  console.log(`   ♻️ Reused existing: ${result.stats?.reusedExisting || 0}`)
  console.log(`   ❌ Errors: ${result.errors.length}`)
  
  return result
}

export async function acceptV3HierarchyItemWithParents(
  itemsHierarchy: DreamNode[],
  targetItem: DreamNode | AspirationNode | GoalNode | TaskNode | ActionItemNode | RoadblockNode | KnowledgeSkillNode,
  targetType: string,
  options: AcceptV3ItemOptions = {}
): Promise<AcceptV3ItemResult> {
  const result: AcceptV3ItemResult = {
    success: false,
    createdItems: [],
    errors: [],
    stats: {
      newlyCreated: 0,
      reusedExisting: 0
    }
  }
  
  try {
    // Extract the full parent chain from root to target
    const parentChain = extractParentChainFromV3Hierarchy(itemsHierarchy, targetItem, targetType)
    
    console.log('📦 V3 Parent chain extracted:', parentChain.map(p => `${p.type}: ${p.title}`).join(' → '))
    
    // Track created IDs for linking
    const createdIds: Record<string, number> = {}
    
    // Create items from root to target (top-down)
    for (let i = 0; i < parentChain.length; i++) {
      const chainItem = parentChain[i]
      
      // Check for similar items if not skipped
      if (!options.skipDuplicateCheck) {
        try {
          const similarItems = await checkForSimilarItems(
            chainItem.title,
            chainItem.description || '',
            chainItem.hierarchyType,
            options.similarityThreshold || 0.75
          )
          
          if (similarItems.length > 0) {
            // Found similar item - use existing instead of creating
            const existingItem = similarItems[0].item
            console.log(`✅ Using existing ${chainItem.type}: "${existingItem.title}" (ID: ${existingItem.id})`)
            createdIds[chainItem.type] = existingItem.id
            result.createdItems.push({
              type: chainItem.type,
              id: existingItem.id,
              title: existingItem.title,
              isExisting: true
            })
            
            // Track stats
            if (result.stats) {
              result.stats.reusedExisting++
            }
            
            // Notify callback
            if (options.onItemCreated) {
              options.onItemCreated(chainItem.type, existingItem.id, existingItem.title)
            }
            continue
          }
        } catch (error) {
          console.warn(`Similar items check failed for ${chainItem.type}:`, error)
          // Continue with creation
        }
      }
      
      // Build the request with parent linking
      const request: AcceptItemRequest = {
        title: chainItem.title,
        description: chainItem.description || chainItem.title,
        status: getDefaultStatus(chainItem.hierarchyType)
      }
      
      // Add type-specific fields
      if (chainItem.life_area) {
        request.life_area = chainItem.life_area
      }
      if (chainItem.priority) {
        request.priority = chainItem.priority as any
      }
      
      // Action item specific
      if (chainItem.type === 'action_item' && chainItem.duration_minutes) {
        request.estimated_duration = chainItem.duration_minutes
      }
      
      // Roadblock specific
      if (chainItem.type === 'roadblock') {
        if (chainItem.severity) {
          request.severity = chainItem.severity
        }
        if (chainItem.suggested_solutions) {
          request.suggested_solutions = chainItem.suggested_solutions
        }
      }

      // Decision Signal Capture: forward decision fields + provenance for
      // EVERY item in the parent chain (not just the target). Mirrors the
      // single-item save path; the repository normalizes and drops fields
      // that don't apply to the type, so forwarding a superset is safe.
      const chainFields = chainItem.fields ?? {}
      for (const key of DECISION_FIELD_KEYS) {
        const v = chainFields[key] ?? (chainItem as any)[key]
        if (v !== undefined && v !== null && (request as any)[key] === undefined) {
          (request as any)[key] = v
        }
      }
      const chainDecisionMeta = chainItem.decision_meta ?? chainFields.decision_meta
      if (chainDecisionMeta && typeof chainDecisionMeta === 'object') {
        (request as any).decision_meta = chainDecisionMeta
      }

      // Link to parent based on hierarchy
      console.log(`🔗 Linking ${chainItem.type} to parent. Current createdIds:`, Object.keys(createdIds))
      
      if (chainItem.type === 'aspiration' && createdIds['dream']) {
        request.parent_dream_id = createdIds['dream']
        console.log(`  ✅ Set parent_dream_id = ${createdIds['dream']}`)
      } else if (chainItem.type === 'goal') {
        if (createdIds['aspiration']) {
          request.parent_aspiration_id = createdIds['aspiration']
          console.log(`  ✅ Set parent_aspiration_id = ${createdIds['aspiration']}`)
        } else if (createdIds['dream']) {
          request.parent_dream_id = createdIds['dream']
          console.log(`  ✅ Set parent_dream_id = ${createdIds['dream']}`)
        }
      } else if (chainItem.type === 'task') {
        if (createdIds['goal']) {
          request.parent_goal_id = createdIds['goal']
          console.log(`  ✅ Set parent_goal_id = ${createdIds['goal']}`)
        } else {
          console.log(`  ⚠️ No goal in createdIds for task! Available:`, Object.keys(createdIds))
        }
      } else if (chainItem.type === 'action_item') {
        if (createdIds['task']) {
          request.parent_task_id = createdIds['task']
          console.log(`  ✅ Set parent_task_id = ${createdIds['task']}`)
        } else {
          console.log(`  ⚠️ No task in createdIds for action_item! Available:`, Object.keys(createdIds))
        }
      } else if (chainItem.type === 'roadblock') {
        if (createdIds['goal']) {
          request.parent_goal_id = createdIds['goal']
          console.log(`  ✅ Set parent_goal_id = ${createdIds['goal']} for roadblock`)
        }
      } else if (chainItem.type === 'skill') {
        if (createdIds['goal']) {
          request.parent_goal_id = createdIds['goal']
          console.log(`  ✅ Set parent_goal_id = ${createdIds['goal']} for skill`)
        }
      } else if (chainItem.type === 'habit') {
        if (createdIds['goal']) {
          request.parent_goal_id = createdIds['goal']
          console.log(`  ✅ Set parent_goal_id = ${createdIds['goal']} for habit`)
        }
      }
      
      console.log(`📤 Creating ${chainItem.type} with request:`, request)
      
      // Create the item
      try {
        let createdItem: any
        
        if (chainItem.hierarchyType === 'DREAMS') {
          createdItem = await dreamService.createDream(request as any)
        } else if (chainItem.hierarchyType === 'ASPIRATIONS_AMBITIONS') {
          createdItem = await aspirationService.createAspiration(request as any)
        } else if (chainItem.hierarchyType === 'GOALS_OBJECTIVES') {
          createdItem = await goalService.createGoal(request as any)
          console.log(`🔍 Goal creation response:`, createdItem)
        } else if (chainItem.hierarchyType === 'TASKS_ACTIONS') {
          createdItem = await taskService.createTask(request as any)
          console.log(`🔍 Task creation response:`, createdItem)
        } else if (chainItem.hierarchyType === 'ACTION_ITEMS') {
          createdItem = await actionItemService.createActionItem(request as any)
        } else if (chainItem.hierarchyType === 'ROADBLOCKS') {
          createdItem = await roadblockService.createRoadblock(request as any)
        } else if (chainItem.hierarchyType === 'SKILLS') {
          createdItem = await skillService.createSkill(request as any)
          console.log(`🔍 Skill creation response:`, createdItem)
        } else if (chainItem.hierarchyType === 'HABITS') {
          createdItem = await habitService.createHabit(request as any)
          console.log(`🔍 Habit creation response:`, createdItem)
        }
        
        console.log(`🔍 Extracting ID from createdItem. Type: ${chainItem.type}, createdItem.id: ${createdItem?.id}, createdItem.data?.id: ${createdItem?.data?.id}`)
        const itemId = createdItem?.id || createdItem?.data?.id
        if (itemId) {
          createdIds[chainItem.type] = itemId
          chainItem.createdId = itemId
          
          // Same as the parent-chain path above: a 201 whose id is a row that
          // already existed is a MERGE, not a creation.
          const dedup = readCreateDedup(createdItem)

          result.createdItems.push({
            type: chainItem.type,
            id: itemId,
            title: chainItem.title,
            isExisting: dedup.deduped,
            ...(dedup.deduped ? { dedup } : {})
          })

          // Track stats
          if (result.stats) {
            if (dedup.deduped) result.stats.reusedExisting++
            else result.stats.newlyCreated++
          }

          console.log(dedup.deduped
            ? `♻️  Merged ${chainItem.type}: "${chainItem.title}" onto existing #${itemId} ("${dedup.intoTitle}")`
            : `✅ Created ${chainItem.type}: "${chainItem.title}" (ID: ${itemId})`)
          console.log(`📝 Updated createdIds map:`, Object.keys(createdIds), '=', Object.values(createdIds))
          
          // Notify callback
          if (options.onItemCreated) {
            options.onItemCreated(chainItem.type, itemId, chainItem.title)
          }
          
          // Track target item
          if (chainItem.title === targetItem.title && chainItem.type === targetType) {
            result.targetItem = createdItem
          }
        } else {
          console.error(`❌ No ID returned for ${chainItem.type}: "${chainItem.title}"`, createdItem)
        }
      } catch (createError: any) {
        console.error(`Failed to create ${chainItem.type}:`, createError)
        result.errors.push(`Failed to create ${chainItem.type} "${chainItem.title}": ${createError.message}`)
        // Continue with remaining items even if one fails
      }
    }
    
    result.success = result.createdItems.length > 0
    
  } catch (error: any) {
    console.error('Error in acceptV3HierarchyItemWithParents:', error)
    result.errors.push(error.message)
  }
  
  return result
}

/**
 * Accept just the target item (without parents) from V3 hierarchy.
 * Useful when the user explicitly doesn't want parent items created.
 */
export async function acceptV3HierarchyItemOnly(
  targetItem: DreamNode | AspirationNode | GoalNode | TaskNode | ActionItemNode | RoadblockNode | KnowledgeSkillNode,
  targetType: string,
  options: AcceptV3ItemOptions = {}
): Promise<any> {
  const hierarchyType = mapV3TypeToHierarchyType(targetType)
  
  // Convert V3 node to HierarchyItem format
  const item: HierarchyItem = {
    id: `v3_${Date.now()}`,
    type: hierarchyType,
    classification: {
      type: hierarchyType,
      confidence: 1.0
    },
    status: 'draft',
    details: {
      clean_summary: targetItem.title,
      description: targetItem.description,
      life_area: (targetItem as any).life_area,
      priority: (targetItem as any).priority
    }
  }
  
  return acceptHierarchyItem(item, hierarchyType, {
    checkDuplicates: !options.skipDuplicateCheck,
    onSimilarItemsFound: options.onSimilarItemsFound,
    similarityThreshold: options.similarityThreshold
  })
}

// Re-export HierarchyItem type
export type { HierarchyItem }

